/**
 * useUnsavedWarning — warn before the BROWSER discards half-typed work
 * (Card PARTNER-LIST-REFRESH, 8 Oct 2026).
 *
 * ── WHY THIS IS NOT useNavGuard ─────────────────────────────────────────────
 *
 * src/lib/NavHistory.js already has useNavGuard, and it is the right tool for
 * IN-APP navigation: the Back button, the keyboard shortcut, the sidebar, the
 * Capacitor hardware back. It consults a predicate and can CANCEL the move.
 *
 * It cannot see a browser reload, a tab close, or the address bar — nothing in
 * the React tree can. Before this module there were ZERO `beforeunload`
 * handlers in the repository (grep across src/ and portal/), so pressing F5 on
 * a contact with a half-written note destroyed it silently.
 *
 * Now that a reload RESTORES the record (src/lib/urlState.js), reloading
 * becomes a thing staff will do on purpose — which makes the silent loss more
 * likely, not less. The two changes belong in the same card for that reason.
 *
 * ── ⛔⛔ WHY THE DRAFT IS NOT SAVED ANYWHERE ────────────────────────────────
 *
 * The obvious fix is to stash the draft and restore it. We deliberately do not.
 * A half-written clinical note or an SMS to a patient IS PHI, and localStorage
 * and sessionStorage are both plaintext, both survive the tab, and both sit on
 * a workstation that may be shared. src/components/CrmFilterBar already refuses
 * to persist a CRM search for exactly this reason.
 *
 * ⇒ We warn, and we lose the draft if the human says go ahead. That is a worse
 * user experience than restoring it and a much better privacy posture, and the
 * trade is stated here so nobody "improves" it later without re-deciding it.
 *
 * ── WHAT THE BROWSER ACTUALLY SHOWS ────────────────────────────────────────
 *
 * Modern browsers IGNORE a custom string and show their own generic prompt, so
 * `message` is not displayed to the user. It is still required, and still
 * GENERIC, because it is what the matching useNavGuard registration shows for
 * in-app navigation — and because a future caller reading this signature must
 * not think a patient name would be acceptable here.
 */
import { useEffect, useRef } from 'react';

/**
 * @param {boolean} when     true = there is unsaved text right now
 * @param {string}  message  GENERIC description. NEVER a name, id or body text.
 */
export default function useUnsavedWarning(when, message = 'You have unsaved work.') {
  // Read through a ref so flipping `when` never re-registers the listener —
  // the same shape useNavGuard uses, and for the same reason.
  const whenRef = useRef(when);
  whenRef.current = when;
  const messageRef = useRef(message);
  messageRef.current = message;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.addEventListener) return undefined;
    const onBeforeUnload = (e) => {
      if (!whenRef.current) return undefined;
      // The three-part incantation every browser generation needs. Chrome and
      // Safari act on preventDefault + returnValue; older Firefox acts on the
      // returned string. Setting all three is the only form that works across
      // the browsers staff actually use, and costs nothing.
      e.preventDefault();
      e.returnValue = messageRef.current;
      return messageRef.current;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // Empty deps ON PURPOSE: the listener reads refs, so it is correct for the
    // component's whole lifetime and must not be torn down on every keystroke.
  }, []);
}
